/**
 * VIVA HUB canonical groups and app access mapping (frontend)
 *
 * Three user types:
 * - Admin (hub-admin): all apps + scheduler admin
 * - VIVA Associate (viva-associate): HUB, CRM, Room Scheduler, Profile, Email Signature
 * - Renter (renter): HUB, Room Scheduler, Profile
 * - CRM Assignee (crm-assignee): supplementary assignee roster
 * - CRM Notification (crm-notify): supplementary notification roster
 * - CRM Nudge (crm-nudge): send “Nudge team member” in CRM
 */

const SCHEDULER_PERMISSIONS = [
  'manage_rooms',
  'manage_users',
  'manage_groups',
  'view_reports',
  'manage_reservations',
]

export const HUB_GROUPS = {
  HUB_ADMIN: 'hub-admin',
  VIVA_ASSOCIATE: 'viva-associate',
  RENTER: 'renter',
  CRM_ASSIGNEE: 'crm-assignee',
  CRM_NOTIFY: 'crm-notify',
  CRM_NUDGE: 'crm-nudge',
  /** @deprecated Use CRM_ASSIGNEE */
  CRM_LEADS: 'crm-assignee',
}

export const HUB_SUPPLEMENTARY_GROUP_NAMES = [
  HUB_GROUPS.CRM_ASSIGNEE,
  HUB_GROUPS.CRM_NOTIFY,
  HUB_GROUPS.CRM_NUDGE,
]

export const HUB_PRIMARY_GROUP_NAMES = [
  HUB_GROUPS.HUB_ADMIN,
  HUB_GROUPS.VIVA_ASSOCIATE,
  HUB_GROUPS.RENTER,
]

export const DEPRECATED_GROUP_NAMES = [
  'hub-user',
  'viva-team',
  'roomscheduler-user',
  'roomscheduler-admin',
]

export const GROUP_DEFAULT_PERMISSIONS = {
  [HUB_GROUPS.HUB_ADMIN]: [
    'access_room_scheduler',
    'access_audit_log',
    'access_crm',
    'access_whatsapp',
    'access_settings',
    'access_groups',
    'access_profile',
    'access_email_signature',
    ...SCHEDULER_PERMISSIONS,
  ],
  [HUB_GROUPS.VIVA_ASSOCIATE]: [
    'access_crm',
    'access_room_scheduler',
    'access_profile',
    'access_email_signature',
    'manage_reservations',
  ],
  [HUB_GROUPS.RENTER]: [
    'access_room_scheduler',
    'access_profile',
    'manage_reservations',
  ],
  [HUB_GROUPS.CRM_ASSIGNEE]: ['crm_lead_assignee', 'access_crm'],
  [HUB_GROUPS.CRM_NOTIFY]: ['crm_lead_notify'],
  [HUB_GROUPS.CRM_NUDGE]: ['crm_lead_nudge', 'access_crm'],
}

export const PERMISSION_ALLOWED_GROUPS = {
  access_audit_log: [HUB_GROUPS.HUB_ADMIN],
  access_settings: [HUB_GROUPS.HUB_ADMIN],
  access_groups: [HUB_GROUPS.HUB_ADMIN],
  access_contact: [HUB_GROUPS.HUB_ADMIN],
  access_client_intake: [HUB_GROUPS.HUB_ADMIN],
  access_whatsapp: [HUB_GROUPS.HUB_ADMIN],
  access_crm: [HUB_GROUPS.HUB_ADMIN, HUB_GROUPS.VIVA_ASSOCIATE],
  access_profile: [HUB_GROUPS.HUB_ADMIN, HUB_GROUPS.VIVA_ASSOCIATE, HUB_GROUPS.RENTER],
  access_email_signature: [HUB_GROUPS.HUB_ADMIN, HUB_GROUPS.VIVA_ASSOCIATE],
  access_room_scheduler: [HUB_GROUPS.HUB_ADMIN, HUB_GROUPS.VIVA_ASSOCIATE, HUB_GROUPS.RENTER],
  manage_reservations: [HUB_GROUPS.VIVA_ASSOCIATE, HUB_GROUPS.RENTER, HUB_GROUPS.HUB_ADMIN],
  manage_rooms: [HUB_GROUPS.HUB_ADMIN],
  view_reports: [HUB_GROUPS.HUB_ADMIN],
  manage_users: [HUB_GROUPS.HUB_ADMIN],
  manage_groups: [HUB_GROUPS.HUB_ADMIN],
}

export const GROUP_LABELS = {
  [HUB_GROUPS.HUB_ADMIN]: 'Admin',
  [HUB_GROUPS.VIVA_ASSOCIATE]: 'VIVA Associate',
  [HUB_GROUPS.RENTER]: 'Renter',
  [HUB_GROUPS.CRM_ASSIGNEE]: 'CRM Assignee',
  [HUB_GROUPS.CRM_NOTIFY]: 'CRM Notification',
  [HUB_GROUPS.CRM_NUDGE]: 'CRM Nudge',
  [HUB_GROUPS.CRM_LEADS]: 'CRM Assignee',
}

export const GROUP_APP_ACCESS = {
  [HUB_GROUPS.HUB_ADMIN]: [
    'VIVA HUB (all apps)',
    'Room Scheduler (admin)',
    'CRM',
    'WhatsApp',
    'Users',
    'Groups',
    'Audit Log',
    'Profile',
    'Email Signature',
  ],
  [HUB_GROUPS.VIVA_ASSOCIATE]: ['VIVA HUB', 'CRM', 'Room Scheduler', 'Profile', 'Email Signature'],
  [HUB_GROUPS.RENTER]: ['VIVA HUB', 'Room Scheduler', 'Profile'],
  [HUB_GROUPS.CRM_ASSIGNEE]: ['CRM Assignee roster'],
  [HUB_GROUPS.CRM_NOTIFY]: ['CRM case notifications'],
  [HUB_GROUPS.CRM_NUDGE]: ['CRM case nudge (friendly reminders)'],
}

export function getUserGroupNames(user) {
  if (!user) return []
  if (Array.isArray(user.group_names) && user.group_names.length > 0) {
    return user.group_names
  }
  if (user.group_name) {
    return String(user.group_name)
      .split(',')
      .map((n) => n.trim())
      .filter(Boolean)
  }
  return []
}

export function isHubSuperAdmin(user) {
  return getUserGroupNames(user).includes(HUB_GROUPS.HUB_ADMIN)
}

export function isSchedulerAdmin(user) {
  return isHubSuperAdmin(user)
}

export function canSetOtherUserPassword(user) {
  return getUserGroupNames(user).includes(HUB_GROUPS.HUB_ADMIN)
}

/** @deprecated Use user.permissions from the API (merged from group rows in D1). */
export function hasGroupAppAccess(user, permission) {
  const names = getUserGroupNames(user)
  if (names.length === 0) return false
  const allowedGroups = PERMISSION_ALLOWED_GROUPS[permission]
  if (!allowedGroups) return false
  return names.some((name) => allowedGroups.includes(name))
}

export function hasPermission(user, permission) {
  if (!user) return false
  if (isHubSuperAdmin(user)) return true
  return (user.permissions || []).includes(permission)
}
